import { useState, useEffect, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useRefreshEffect } from "@/context/RefreshContext";
import PageHeader from "@/components/PageHeader";
import RecordsSection from "@/components/RecordsSection";
import { lzInfoColumns, lzInfoFields, lzInfoDefault } from "@/lib/entities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Search, Pencil, Trash2, Inbox, UserSquare2, Clock, RefreshCw, Skull, ArrowDownUp } from "lucide-react";
import { LangzeitPunkte, PunkteBadge } from "@/components/LangzeitPunkte";
import { formatDatum, todayISO, LZ_GEFAHRENSTUFE_VALUES, LZ_GEFAHRENSTUFE_COLOR, LZ_GESCHLECHT } from "@/lib/constants";

const GEFAHR = {
  green: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40",
  amber: "bg-amber-500/15 text-amber-400 border-amber-500/40",
  red: "bg-red-500/15 text-red-400 border-red-500/40",
  black: "bg-black text-zinc-100 border-zinc-500",
};

const GEFAHR_TEXT = {
  green: "text-emerald-400",
  amber: "text-amber-400",
  red: "text-red-400",
  black: "text-zinc-100",
};

const SORT_NEXT = { gesehen: "gefahrenstufe", gefahrenstufe: "punkte", punkte: "gesehen" };
const SORT_LABEL = { gesehen: "Zuletzt gesehen", gefahrenstufe: "Gefahrenstufe", punkte: "Punkte (wenigste zuerst)" };

const GEFAHR_RANK = { "Sehr Hoch": 4, Hoch: 3, Mittel: 2, Niedrig: 1 };

const empty = () => ({
  name: "", geburtstag: "", geschlecht: "", bild: "", zuletzt_gesehen: todayISO(),
  verhalten: "", aktivitaet: "", wichtiges: "", privilegien: "", gefahrenstufe: "Niedrig",
});

export default function Langzeithaeftlinge() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(null);
  const [values, setValues] = useState(empty());
  const [submitting, setSubmitting] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [sortMode, setSortMode] = useState("gesehen");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/langzeithaeftlinge");
      setRows(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useRefreshEffect(load);

  const set = (k, v) => setValues((p) => ({ ...p, [k]: v }));

  const openCreate = () => { setEditing(null); setValues(empty()); setDialogOpen(true); };
  const openEdit = (o) => { setEditing(o); setValues({ ...empty(), ...o }); setDetail(null); setDialogOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...values };
      ["punkte", "id", "archived", "archive_status", "created_at", "updated_at", "created_by"].forEach((k) => delete payload[k]);
      if (editing) {
        await api.put(`/langzeithaeftlinge/${editing.id}`, payload);
        toast.success("Häftling aktualisiert");
      } else {
        await api.post("/langzeithaeftlinge", payload);
        toast.success("Häftling angelegt");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally {
      setSubmitting(false);
    }
  };

  const markSeen = async (o, e) => {
    e && e.stopPropagation();
    try {
      const { data } = await api.patch(`/langzeithaeftlinge/${o.id}/seen`);
      toast.success("Zuletzt gesehen aktualisiert");
      setRows((rs) => rs.map((r) => (r.id === o.id ? data : r)));
      setDetail((d) => (d && d.id === o.id ? data : d));
    } catch (e2) {
      toast.error("Fehlgeschlagen");
    }
  };

  const doDelete = async () => {
    if (!confirm) return;
    try {
      await api.delete(`/langzeithaeftlinge/${confirm.id}`);
      toast.success("Häftling gelöscht");
      load();
    } catch (e) {
      toast.error("Fehlgeschlagen");
    } finally {
      setConfirm(null);
    }
  };

  const setPunkte = useCallback((id, p) => {
    setRows((rs) => rs.map((r) => (r.id === id && r.punkte !== p ? { ...r, punkte: p } : r)));
    setDetail((d) => (d && d.id === id && d.punkte !== p ? { ...d, punkte: p } : d));
  }, []);
  const detailId = detail?.id;
  const onPunkte = useCallback((p) => setPunkte(detailId, p), [setPunkte, detailId]);

  const filtered = rows.filter((r) =>
    !search.trim() ? true : [r.name, r.gefahrenstufe, r.wichtiges].some((v) => String(v ?? "").toLowerCase().includes(search.toLowerCase()))
  );

  const sorted = [...filtered].sort((a, b) => {
    if (sortMode === "punkte") {
      return (a.punkte || 0) - (b.punkte || 0);
    }
    if (sortMode === "gefahrenstufe") {
      return (GEFAHR_RANK[b.gefahrenstufe] || 0) - (GEFAHR_RANK[a.gefahrenstufe] || 0);
    }
    // zuletzt gesehen: neu -> alt (leere Daten nach unten)
    const da = a.zuletzt_gesehen || "";
    const db = b.zuletzt_gesehen || "";
    if (!da && !db) return 0;
    if (!da) return 1;
    if (!db) return -1;
    return db.localeCompare(da);
  });

  const Photo = ({ src, className }) =>
    src ? (
      <img src={src} alt="" className={className} onError={(e) => { e.currentTarget.style.display = "none"; }} />
    ) : null;

  return (
    <div>
      <PageHeader eyebrow="SADCR · Vollzug" title="Langzeithäftlinge" description="Profile langfristig inhaftierter Personen." icon={UserSquare2} />
      <div className="p-8 space-y-8">
        <section className="space-y-3">
          <h2 className="font-heading text-sm font-bold uppercase tracking-wide text-zinc-100">Infos</h2>
          <RecordsSection
            endpoint="/langzeit-infos"
            columns={lzInfoColumns}
            fields={lzInfoFields}
            makeDefault={lzInfoDefault()}
            createLabel="Info erstellen"
            emptyLabel="Keine Infos vorhanden"
            testidPrefix="lzinfo"
            showArchive={false}
            dateSortKey="datum"
          />
        </section>

        <section className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <Input data-testid="lz-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Häftling suchen…" className="pl-9 bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary" />
          </div>
          <Button data-testid="lz-sort-btn" onClick={() => setSortMode((m) => SORT_NEXT[m])} variant="outline" className="ml-auto rounded-sm border-zinc-700 gap-2">
            <ArrowDownUp className="h-4 w-4" />
            Sortierung: {SORT_LABEL[sortMode]}
          </Button>
          <Button data-testid="lz-create-btn" onClick={openCreate} className="rounded-sm bg-primary hover:bg-blue-600 text-white gap-2">
            <Plus className="h-4 w-4" /> Häftling anlegen
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-10 text-zinc-500 font-mono text-sm">Wird geladen…</div>
        ) : filtered.length === 0 ? (
          <div className="border border-zinc-800 rounded-sm bg-[#0C0E12] py-16 flex flex-col items-center gap-2 text-zinc-600">
            <Inbox className="h-8 w-8" /><span className="font-mono text-sm">Keine Langzeithäftlinge erfasst</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {sorted.map((o) => {
              const gc = LZ_GEFAHRENSTUFE_COLOR[o.gefahrenstufe];
              return (
                <div key={o.id} data-testid="lz-card" onClick={() => setDetail(o)} className="group cursor-pointer bg-[#0C0E12] border border-zinc-800 rounded-sm overflow-hidden hover:border-zinc-600 transition-colors duration-150 sadcr-fade">
                  <div className="relative h-44 bg-zinc-900 flex items-center justify-center">
                    <Photo src={o.bild} className="absolute inset-0 w-full h-full object-cover" />
                    <UserSquare2 className="h-10 w-10 text-zinc-700" />
                  </div>
                  <div className="p-4 space-y-2">
                    <h3 className="font-heading text-base font-bold text-zinc-100 truncate">{o.name}</h3>
                    <div className="flex flex-wrap gap-1.5">
                      {o.gefahrenstufe && (
                        <Badge className={`rounded-sm border font-mono text-[10px] gap-1 ${GEFAHR[gc] || ""}`}>
                          {gc === "black" && <Skull className="h-3 w-3" />} {o.gefahrenstufe}
                        </Badge>
                      )}
                      <PunkteBadge punkte={o.punkte} testid="lz-card-punkte" />
                    </div>
                    <div className="flex items-center gap-2 text-xs text-zinc-500">
                      <Clock className="h-3.5 w-3.5" />
                      <span className="font-mono">{o.zuletzt_gesehen ? formatDatum(o.zuletzt_gesehen) : "—"}</span>
                      <button data-testid="lz-seen" onClick={(e) => markSeen(o, e)} title="Auf heute setzen" className="ml-auto text-zinc-500 hover:text-primary transition-colors duration-150">
                        <RefreshCw className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </section>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-[#0C0E12] border-zinc-800 rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading uppercase tracking-tight text-zinc-100 flex items-center gap-3">
                  {detail.name}
                  {detail.gefahrenstufe && (
                    <Badge className={`rounded-sm border font-mono text-[10px] gap-1 ${GEFAHR[LZ_GEFAHRENSTUFE_COLOR[detail.gefahrenstufe]] || ""}`}>
                      {LZ_GEFAHRENSTUFE_COLOR[detail.gefahrenstufe] === "black" && <Skull className="h-3 w-3" />} {detail.gefahrenstufe}
                    </Badge>
                  )}
                  <PunkteBadge punkte={detail.punkte} testid="lz-detail-punkte" />
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                {detail.bild && <img src={detail.bild} alt="" className="w-full h-auto max-h-[70vh] object-contain rounded-sm border border-zinc-800 bg-black/30" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
                <div className="grid grid-cols-2 gap-3">
                  <Info label="Geburtstag" value={detail.geburtstag ? formatDatum(detail.geburtstag) : "—"} mono />
                  <Info label="Geschlecht" value={detail.geschlecht} />
                  <div className="col-span-2 flex items-end gap-2">
                    <div className="flex-1"><Info label="Zuletzt gesehen" value={detail.zuletzt_gesehen ? formatDatum(detail.zuletzt_gesehen) : "—"} mono /></div>
                    <Button size="sm" variant="outline" onClick={(e) => markSeen(detail, e)} className="rounded-sm border-zinc-700 gap-1.5 h-8"><RefreshCw className="h-3.5 w-3.5" /> Heute</Button>
                  </div>
                  <Info label="Verhalten" value={detail.verhalten} />
                  <Info label="Aktivität" value={detail.aktivitaet} />
                  <div className="col-span-2"><Info label="Wichtiges" value={detail.wichtiges} /></div>
                  <div className="col-span-2"><Info label="Privilegien" value={detail.privilegien} /></div>
                </div>
                <LangzeitPunkte haeftlingId={detail.id} onChange={onPunkte} />
              </div>
              <DialogFooter className="pt-2 gap-2">
                <Button variant="ghost" onClick={() => setConfirm(detail)} className="rounded-sm text-red-400 hover:bg-red-500/10 gap-2 mr-auto"><Trash2 className="h-4 w-4" /> Löschen</Button>
                <Button variant="outline" onClick={() => openEdit(detail)} className="rounded-sm border-zinc-700 gap-2"><Pencil className="h-4 w-4" /> Bearbeiten</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0C0E12] border-zinc-800 rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight text-zinc-100">{editing ? "Häftling bearbeiten" : "Häftling anlegen"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Name" required><Input data-testid="lz-name" value={values.name} onChange={(e) => set("name", e.target.value)} required className="bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary" /></Field>
              <Field label="Geburtstag"><Input type="date" data-testid="lz-geburtstag" value={values.geburtstag} onChange={(e) => set("geburtstag", e.target.value)} className="bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary" /></Field>
              <Field label="Geschlecht">
                <SelectBox testid="lz-geschlecht" value={values.geschlecht} onChange={(v) => set("geschlecht", v)} options={LZ_GESCHLECHT} />
              </Field>
              <Field label="Gefahrenstufe">
                <SelectBox testid="lz-gefahrenstufe" value={values.gefahrenstufe} onChange={(v) => set("gefahrenstufe", v)} options={LZ_GEFAHRENSTUFE_VALUES} />
              </Field>
            </div>
            <Field label="Bild (Link, z.B. Gyazo)"><Input data-testid="lz-bild" value={values.bild} onChange={(e) => set("bild", e.target.value)} placeholder="https://i.gyazo.com/…" className="bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary" /></Field>
            <Field label="Zuletzt gesehen"><Input type="date" data-testid="lz-zuletzt" value={values.zuletzt_gesehen} onChange={(e) => set("zuletzt_gesehen", e.target.value)} className="bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Verhalten"><Input data-testid="lz-verhalten" value={values.verhalten} onChange={(e) => set("verhalten", e.target.value)} className="bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary" /></Field>
              <Field label="Aktivität"><Input data-testid="lz-aktivitaet" value={values.aktivitaet} onChange={(e) => set("aktivitaet", e.target.value)} className="bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary" /></Field>
            </div>
            <Field label="Wichtiges"><Textarea data-testid="lz-wichtiges" value={values.wichtiges} onChange={(e) => set("wichtiges", e.target.value)} rows={2} className="bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary resize-none" /></Field>
            <Field label="Privilegien"><Textarea data-testid="lz-privilegien" value={values.privilegien} onChange={(e) => set("privilegien", e.target.value)} rows={2} className="bg-transparent border-zinc-700 rounded-sm focus-visible:ring-primary resize-none" /></Field>
            <DialogFooter className="pt-2 gap-2">
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="rounded-sm text-zinc-400">Abbrechen</Button>
              <Button type="submit" disabled={submitting} data-testid="lz-submit" className="rounded-sm bg-primary hover:bg-blue-600 text-white">{submitting ? "Speichern…" : "Speichern"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-[#0C0E12] border-zinc-800 rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-zinc-100">Häftling löschen?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">Dieser Vorgang kann nicht rückgängig gemacht werden.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm bg-transparent border-zinc-700 text-zinc-300">Abbrechen</AlertDialogCancel>
            <AlertDialogAction data-testid="lz-confirm" onClick={() => { doDelete(); setDetail(null); }} className="rounded-sm bg-red-600 hover:bg-red-700 text-white">Bestätigen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-500">{label}{required && <span className="text-red-500 ml-1">*</span>}</Label>
      {children}
    </div>
  );
}

function Info({ label, value, mono, accent }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-600">{label}</div>
      <div className={`${mono ? "font-mono" : ""} ${accent || "text-zinc-200"} text-sm mt-0.5 whitespace-pre-wrap`}>{value || <span className="text-zinc-600">—</span>}</div>
    </div>
  );
}

function SelectBox({ value, onChange, options, testid }) {
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="bg-transparent border-zinc-700 rounded-sm focus:ring-primary"><SelectValue placeholder="Auswählen…" /></SelectTrigger>
      <SelectContent className="bg-[#0C0E12] border-zinc-800 rounded-sm">
        {options.map((o) => <SelectItem key={o} value={o} className="rounded-sm text-sm">{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
